<script setup>
import { ref, computed, onMounted, watch, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Modal from '../../../../components/Modal.vue';
import { format } from 'date-fns'; // Import date-fns for formatting
import FormInput from 'v3/components/Form/Input.vue';
// Ciclo 9 — date picker próprio do app (mesma primitiva de CustomSnoozeModal/
// DateRangePicker, tematizada em plugins/_date-picker.scss). O <input
// type="date"> nativo renderiza a máscara no locale do BROWSER (mm/dd/yyyy em
// browsers en-US) e não há atributo para forçar formato; aqui fixamos a
// exibição em DD/MM/YYYY mantendo o contrato interno YYYY-MM-DD (presets,
// quick filters e filtro server-side intocados).
import DatePicker from 'vue-datepicker-next';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAlert } from 'dashboard/composables';
import { isSet, parseDateOnlyLocal } from 'dashboard/helper/filterHelper';
import { reportingDatePreset } from '../helpers/reportingDatePresets';
import Select from 'dashboard/components-next/select/Select.vue';
import { getStageColorClasses, stageColorStyle } from './pipelineTemplates';

import { REPORTING_DEFAULT_TIMEZONE } from 'dashboard/helper/reportingTimezone';
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  filteredResults: {
    type: Object,
    default: () => ({ total: 0, stages: {} }),
  },
  currentPipeline: {
    type: Object,
    default: null,
  },
  size: {
    type: String,
    default: 'medium',
  },
  initialFilters: {
    type: Object,
    default: null,
  },
  scope: {
    type: String,
    default: 'global', // 'global' or 'column'
  },
  columnName: {
    type: String,
    default: '',
  },
  // eslint-disable-next-line vue/no-unused-properties
  columns: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['apply', 'close']);
const { t } = useI18n();
const store = useStore();
const { showAlert } = useAlert();

// Ciclo 8 (a11y) — prefixo único por instância para os ids dos campos do
// formulário: o componente monta no tab global E em cada coluna (lazy), então
// ids fixos duplicariam no DOM e quebrariam a associação label↔campo.
const uid = useId();
const fieldId = suffix => `${uid}-${suffix}`;

// Fonte única do formato de filtro — evita perder chaves (ex.: labels) em
// algum dos vários pontos de reset.
const blankFilters = () => ({
  priority: [],
  value: { min: null, max: null },
  agent_id: null,
  date: { start: null, end: null },
  status: null, // 'won', 'lost', 'open', or null for all
  stages: [], // Filter by specific stages (B4 - Advanced Filters)
  sla_exceeded: false, // Fase 2.1 — só cards que excederam SLA
  labels: [], // NOOVI: filtra por labels da conversa vinculada (server-side)
});

const filters = ref(blankFilters());

// Account-scoped timezone is the source of truth for every relative date
// preset, so "today" remains today for the account instead of the browser.
const currentUserId = computed(() => store.getters.getCurrentUserID || 0);
const currentAccountId = computed(() => store.getters.getCurrentAccountId || 0);
const currentAccount = computed(
  () =>
    store.getters['accounts/getAccount']?.(currentAccountId.value) ||
    store.getters.getCurrentAccount
);
const reportingTimezone = computed(
  () =>
    currentAccount.value?.reporting_timezone ||
    currentAccount.value?.settings?.reporting_timezone ||
    REPORTING_DEFAULT_TIMEZONE
);

// Quick filter presets (B4 - Advanced Filters)
const quickFilters = [
  {
    id: 'today',
    label: 'PIPELINE.QUICK_FILTERS.TODAY',
    icon: 'calendar',
    getFilters: () => ({
      date: reportingDatePreset('today', {
        reportingTimezone: reportingTimezone.value,
      }),
    }),
  },
  {
    id: 'this_week',
    label: 'PIPELINE.QUICK_FILTERS.THIS_WEEK',
    icon: 'calendar-clock',
    getFilters: () => ({
      date: reportingDatePreset('this_week', {
        reportingTimezone: reportingTimezone.value,
      }),
    }),
  },
  {
    id: 'overdue',
    label: 'PIPELINE.QUICK_FILTERS.OVERDUE',
    icon: 'warning',
    getFilters: () => ({
      date: reportingDatePreset('overdue', {
        reportingTimezone: reportingTimezone.value,
      }),
      status: 'open',
    }),
  },
  {
    id: 'high_value',
    label: 'PIPELINE.QUICK_FILTERS.HIGH_VALUE',
    icon: 'tag',
    getFilters: () => ({ value: { min: 10000, max: null } }),
  },
  {
    id: 'urgent',
    label: 'PIPELINE.QUICK_FILTERS.URGENT',
    icon: 'flame',
    getFilters: () => ({ priority: ['urgent', 'high'] }),
  },
];

const activeQuickFilter = ref(null);

// --- Preset State ---
const savedPresets = ref({});
const newPresetName = ref('');
const appliedPresetName = ref('');

// Chave escopada por usuário+conta (mesmo padrão do PipelineHeader) para não
// vazar presets entre usuários/contas no mesmo browser.
const presetStorageKey = computed(
  () =>
    `u${currentUserId.value}_a${currentAccountId.value}_pipeline_filter_presets`
);

// Function to check if current filters match any preset
const checkAppliedPreset = currentFilters => {
  if (!currentFilters) return '';
  const matchedName = Object.keys(savedPresets.value).find(name => {
    // Deep comparison, ensure structure matches
    const preset = savedPresets.value[name];
    const current = JSON.parse(JSON.stringify(currentFilters)); // Deep copy for safety
    // Normalize empty values for comparison if necessary
    preset.priority = preset.priority || [];
    preset.value = preset.value || {};
    preset.date = preset.date || {};
    current.priority = current.priority || [];
    current.value = current.value || {};
    current.date = current.date || {};

    return JSON.stringify(preset) === JSON.stringify(current);
  });
  return matchedName || '';
};

// --- Load Presets (mount, key change, modal open) ---
const loadPresets = () => {
  const storedPresets = localStorage.getItem(presetStorageKey.value);
  if (storedPresets) {
    try {
      savedPresets.value = JSON.parse(storedPresets);
    } catch (e) {
      savedPresets.value = {};
    }
  } else {
    // No presets under this key — drop any in-memory presets from a previous
    // user/account so they can't be re-saved under the new scoped key.
    savedPresets.value = {};
  }
};

// User/account switch changes the scoped key — reload immediately so presets
// never leak across scopes within a long-lived component instance.
watch(presetStorageKey, loadPresets);

// Watch for the modal becoming visible or initialFilters changing
watch(
  () => [props.show, props.initialFilters],
  ([newShow, newInitialFilters]) => {
    if (newShow) {
      // Re-read presets on open — storage may have changed in another tab or
      // the scoped key may have changed since mount.
      loadPresets();
      // Default filter structure
      const defaultFilters = blankFilters();

      // When modal opens, load the initial state from the column
      if (newInitialFilters) {
        // Deep copy and merge with defaults to ensure all fields exist
        const loadedFilters = JSON.parse(JSON.stringify(newInitialFilters));
        filters.value = { ...defaultFilters, ...loadedFilters };

        // Ensure arrays are initialized (for legacy presets)
        if (!Array.isArray(filters.value.priority)) {
          filters.value.priority = [];
        }
        if (!Array.isArray(filters.value.stages)) {
          filters.value.stages = [];
        }

        // Ensure agent_id is a number if it's not null
        if (
          filters.value.agent_id !== null &&
          filters.value.agent_id !== undefined
        ) {
          filters.value.agent_id = Number(filters.value.agent_id);
        }
      } else {
        // Reset to default if no initial filters provided
        filters.value = defaultFilters;
      }
      // Check if the loaded filters match a preset
      appliedPresetName.value = checkAppliedPreset(filters.value);
    }
  },
  { immediate: true, deep: true }
);

// Watch for changes to agent_id to ensure it's always a number when selected
watch(
  () => filters.value.agent_id,
  newValue => {
    if (newValue !== null && newValue !== undefined) {
      // Convert to number to ensure consistent types
      filters.value.agent_id = Number(newValue);
    }
  }
);

onMounted(() => {
  loadPresets();
  // Guard por uiFlags além do length: N instâncias montadas no mesmo tick
  // viam o store ainda vazio e disparavam N fetches paralelos.
  if (
    store.getters['agents/getAgents'].length === 0 &&
    !store.getters['agents/getUIFlags'].isFetching
  ) {
    store.dispatch('agents/get');
  }
  if (
    store.getters['labels/getLabels'].length === 0 &&
    !store.getters['labels/getUIFlags'].isFetching
  ) {
    store.dispatch('labels/get');
  }
});
// ---------------------

const priorityOptions = computed(() => [
  {
    label: t('PIPELINE.PRIORITY_LABELS.HIGH'),
    value: 'high',
    colors: {
      border: filters.value.priority.includes('high')
        ? 'border-n-orange-9'
        : 'border-n-weak',
      text: filters.value.priority.includes('high')
        ? 'text-n-orange-11'
        : 'text-n-slate-11',
      hover: 'hover:bg-n-alpha-1',
      selected: 'bg-n-orange-3 border-n-orange-9',
      dot: 'bg-n-orange-9',
    },
  },
  {
    label: t('PIPELINE.PRIORITY_LABELS.MEDIUM'),
    value: 'medium',
    colors: {
      border: filters.value.priority.includes('medium')
        ? 'border-n-amber-9'
        : 'border-n-weak',
      text: filters.value.priority.includes('medium')
        ? 'text-n-amber-11'
        : 'text-n-slate-11',
      hover: 'hover:bg-n-alpha-1',
      selected: 'bg-n-amber-3 border-n-amber-9',
      dot: 'bg-n-amber-9',
    },
  },
  {
    label: t('PIPELINE.PRIORITY_LABELS.LOW'),
    value: 'low',
    colors: {
      border: filters.value.priority.includes('low')
        ? 'border-n-blue-9'
        : 'border-n-weak',
      text: filters.value.priority.includes('low')
        ? 'text-n-blue-11'
        : 'text-n-slate-11',
      hover: 'hover:bg-n-alpha-1',
      selected: 'bg-n-blue-3 border-n-blue-9',
      dot: 'bg-n-blue-9',
    },
  },
  {
    label: t('PIPELINE.PRIORITY_LABELS.URGENT'),
    value: 'urgent',
    colors: {
      border: filters.value.priority.includes('urgent')
        ? 'border-n-ruby-9'
        : 'border-n-weak',
      text: filters.value.priority.includes('urgent')
        ? 'text-n-ruby-11'
        : 'text-n-slate-11',
      hover: 'hover:bg-n-alpha-1',
      selected: 'bg-n-ruby-3 border-n-ruby-9',
      dot: 'bg-n-ruby-9',
    },
  },
]);

const agents = computed(() => {
  return store.getters['agents/getAgents'] || [];
});
const agentOptions = computed(() => [
  { value: '', label: t('PIPELINE.FILTER.AGENT.ALL') },
  { value: -1, label: t('PIPELINE.FILTER.AGENT.NONE') },
  ...agents.value.map(agent => ({ value: agent.id, label: agent.name })),
]);
const selectedAgentId = computed({
  get: () => filters.value.agent_id ?? '',
  set: value => {
    filters.value.agent_id = value === '' ? null : Number(value);
  },
});

// NOOVI: labels da conta (mesmas etiquetas exibidas nos cards). O filtro envia
// os títulos selecionados; o backend casa contra item_details.conversation.label_list.
const labelOptions = computed(() => store.getters['labels/getLabels'] || []);

const toggleLabel = title => {
  filters.value.labels = filters.value.labels.includes(title)
    ? filters.value.labels.filter(l => l !== title)
    : [...filters.value.labels, title];
};

// Pipeline stages for stage filter (B4 - Advanced Filters)
const availableStages = computed(() => {
  if (!props.currentPipeline?.stages) return [];
  return Object.entries(props.currentPipeline.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name || id,
      color: stage.color || '#64748B',
      position: parseInt(stage.position, 10) || 0,
    }))
    .sort((a, b) => a.position - b.position);
});

// Apply quick filter (B4 - Advanced Filters)
const applyQuickFilter = quickFilter => {
  const quickFilterValues = quickFilter.getFilters();

  // Reset all filters first
  filters.value = blankFilters();

  // Apply the quick filter values
  Object.assign(filters.value, quickFilterValues);
  activeQuickFilter.value = quickFilter.id;
  appliedPresetName.value = '';
};

// Check if a quick filter is active
const isQuickFilterActive = quickFilterId =>
  activeQuickFilter.value === quickFilterId;

// --- Helper to get Agent Name by ID ---
const getAgentName = agentId => {
  if (!agentId) return '';
  const agent = agents.value.find(a => a.id === agentId);
  return agent ? agent.name : `ID: ${agentId}`;
};

// --- Helper Function for Preset Summary ---
const getPresetSummary = presetFilters => {
  if (!presetFilters) return '';
  const parts = [];

  // Priority
  if (presetFilters.priority?.length) {
    parts.push(
      `${t('PIPELINE.PRESET.SUMMARY.PRIORITY')}: ${presetFilters.priority.join(', ')}`
    );
  }

  // Value
  const { min, max } = presetFilters.value || {};
  if (min !== undefined && min !== null && max !== undefined && max !== null) {
    parts.push(`${t('PIPELINE.PRESET.SUMMARY.VALUE')}: ${min}-${max}`);
  } else if (min !== undefined && min !== null) {
    parts.push(`${t('PIPELINE.PRESET.SUMMARY.VALUE')}: >= ${min}`);
  } else if (max !== undefined && max !== null) {
    parts.push(`${t('PIPELINE.PRESET.SUMMARY.VALUE')}: <= ${max}`);
  }

  // Agent
  if (presetFilters.agent_id) {
    parts.push(
      `${t('PIPELINE.PRESET.SUMMARY.AGENT')}: ${getAgentName(presetFilters.agent_id)}`
    );
  }

  // Date
  const { start, end } = presetFilters.date || {};
  if (start && end) {
    try {
      parts.push(
        `${t('PIPELINE.PRESET.SUMMARY.DATE')}: ${format(parseDateOnlyLocal(start), 'dd/MM')} - ${format(parseDateOnlyLocal(end), 'dd/MM')}`
      );
    } catch {
      /* Handle invalid date strings */
    }
  } else if (start) {
    try {
      parts.push(
        `${t('PIPELINE.PRESET.SUMMARY.DATE')}: >= ${format(parseDateOnlyLocal(start), 'dd/MM/yy')}`
      );
    } catch {
      /* Handle invalid date strings */
    }
  } else if (end) {
    try {
      parts.push(
        `${t('PIPELINE.PRESET.SUMMARY.DATE')}: <= ${format(parseDateOnlyLocal(end), 'dd/MM/yy')}`
      );
    } catch {
      /* Handle invalid date strings */
    }
  }

  return parts.join('. ') || t('PIPELINE.NO_FILTER_ACTIVE'); // Return summary or default text
};
// ---------------------------------------

const getStageColor = stageName => {
  if (!props.currentPipeline?.stages) return '#64748B';

  const stage = Object.values(props.currentPipeline.stages).find(
    s => s.name === stageName
  );

  if (!stage) {
    const stageById = Object.values(props.currentPipeline.stages).find(
      s => s.id === stageName
    );
    return stageById?.color || '#64748B';
  }

  return stage.color || '#64748B';
};
const stageColorClasses = color => getStageColorClasses(color);
// Cor fora das 5 conhecidas sai por estilo inline — a tabela de classes não a
// alcança e o filtro mostrava a etapa sem cor.
const stageColorStyles = (color, part) => stageColorStyle(color, part);
const stageNameColorClasses = stageName =>
  getStageColorClasses(getStageColor(stageName));
const stageNameColorStyles = (stageName, part) =>
  stageColorStyle(getStageColor(stageName), part);

const hasActiveFilters = computed(() => {
  const hasFilters = Boolean(
    filters.value.labels.length > 0 ||
      filters.value.priority.length > 0 ||
      filters.value.stages.length > 0 ||
      isSet(filters.value.value.min) ||
      isSet(filters.value.value.max) ||
      filters.value.agent_id ||
      filters.value.date.start ||
      filters.value.date.end ||
      filters.value.status ||
      filters.value.sla_exceeded
  );

  return hasFilters && props.filteredResults.total > 0;
});

const handleApply = () => {
  // Ensure agent_id is a number before sending
  const filtersToApply = JSON.parse(JSON.stringify(filters.value));
  if (
    filtersToApply.agent_id !== null &&
    filtersToApply.agent_id !== undefined
  ) {
    filtersToApply.agent_id = Number(filtersToApply.agent_id);
  }

  // Emit the current filters state (deep copy)
  emit('apply', filtersToApply);
  emit('close');
};

const handleClear = () => {
  const clearedFilters = blankFilters();
  filters.value = JSON.parse(JSON.stringify(clearedFilters));
  appliedPresetName.value = '';
  // Emit the cleared state so the column can remove it from localStorage
  emit('apply', JSON.parse(JSON.stringify(clearedFilters)));
  emit('close');
};

// --- Preset Functions ---
const overwriteDialogRef = ref(null);
const deleteDialogRef = ref(null);
const pendingPresetName = ref('');

const persistPreset = name => {
  // Ensure we save with consistent types
  const filtersToSave = JSON.parse(JSON.stringify(filters.value));
  if (filtersToSave.agent_id !== null && filtersToSave.agent_id !== undefined) {
    filtersToSave.agent_id = Number(filtersToSave.agent_id);
  }

  savedPresets.value[name] = filtersToSave;
  localStorage.setItem(
    presetStorageKey.value,
    JSON.stringify(savedPresets.value)
  );
  newPresetName.value = '';
  appliedPresetName.value = name; // Mark the new preset as applied locally
  showAlert(t('PIPELINE.PRESET.SAVE_SUCCESS', { name }));
};

const saveCurrentPreset = () => {
  const name = newPresetName.value.trim();
  if (!name) {
    showAlert(t('PIPELINE.PRESET.ALERT_ENTER_NAME'));
    return;
  }
  if (savedPresets.value[name]) {
    pendingPresetName.value = name;
    overwriteDialogRef.value?.open();
    return;
  }
  persistPreset(name);
};

const confirmOverwritePreset = () => {
  persistPreset(pendingPresetName.value);
  pendingPresetName.value = '';
  overwriteDialogRef.value?.close();
};

const applyPreset = name => {
  if (savedPresets.value[name]) {
    const loadedPreset = JSON.parse(JSON.stringify(savedPresets.value[name]));
    // Merge with defaults to ensure all fields exist (for legacy presets)
    filters.value = { ...blankFilters(), ...loadedPreset };
    // Ensure arrays are properly initialized
    if (!Array.isArray(filters.value.priority)) {
      filters.value.priority = [];
    }
    if (!Array.isArray(filters.value.stages)) {
      filters.value.stages = [];
    }
    appliedPresetName.value = name;
    // User still needs to click "Apply" to confirm
  }
  // Preset not found - no action needed
};

const deletePreset = name => {
  if (!savedPresets.value[name]) return; // Preset not found - no action needed
  pendingPresetName.value = name;
  deleteDialogRef.value?.open();
};

const confirmDeletePreset = () => {
  const name = pendingPresetName.value;
  if (savedPresets.value[name]) {
    // Check if the preset being deleted is the currently applied one
    const deletingApplied = appliedPresetName.value === name;

    delete savedPresets.value[name];
    localStorage.setItem(
      presetStorageKey.value,
      JSON.stringify(savedPresets.value)
    );

    // If the deleted preset was the one applied, clear the applied name —
    // filters remain until user action.
    if (deletingApplied) {
      appliedPresetName.value = '';
    }
    showAlert(t('PIPELINE.PRESET.DELETE_SUCCESS', { name }));
  }
  pendingPresetName.value = '';
  deleteDialogRef.value?.close();
};
</script>

<template>
  <Teleport to="body">
    <Modal
      v-if="show"
      :show="show"
      :size="props.size"
      :show-close-button="false"
      @close="$emit('close')"
    >
      <div class="p-4">
        <header class="mb-3 flex justify-between items-center">
          <div class="flex items-center gap-2">
            <h3 class="text-heading-3 text-n-slate-12">
              {{
                scope === 'column' && columnName
                  ? t('PIPELINE.FILTER_ITEMS') + ': ' + columnName
                  : t('PIPELINE.FILTER_ITEMS')
              }}
            </h3>
            <div
              v-if="hasActiveFilters && filteredResults.total > 0"
              class="flex gap-1"
            >
              <div
                class="flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-3 text-n-slate-12 whitespace-nowrap gap-1"
              >
                {{ filteredResults.total }} {{ t('PIPELINE.FILTER.RESULTS') }}
              </div>
              <div
                v-for="(count, stageName) in filteredResults.stages"
                :key="stageName"
                class="flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium"
                :class="stageNameColorClasses(stageName).badge"
                :style="stageNameColorStyles(stageName, 'badge')"
              >
                <span class="text-[10px]">{{ stageName }}</span>
                <span
                  class="min-w-[18px] rounded-full bg-n-alpha-2 px-1.5 text-center text-[10px]"
                  >{{ count }}</span
                >
              </div>
            </div>
          </div>
          <button
            type="button"
            :aria-label="t('PIPELINE.FORM.CANCEL')"
            class="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
            @click="$emit('close')"
          >
            <fluent-icon icon="dismiss" size="16" />
          </button>
        </header>

        <!-- Quick Filters (B4 - Advanced Filters) -->
        <div class="mb-4">
          <label class="text-label text-n-slate-11 mb-2 block">
            {{ t('PIPELINE.QUICK_FILTERS.LABEL') }}
          </label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="qf in quickFilters"
              :key="qf.id"
              type="button"
              :aria-pressed="isQuickFilterActive(qf.id)"
              class="inline-flex min-h-11 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors duration-150 ease-in-out motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
              :class="
                isQuickFilterActive(qf.id)
                  ? 'bg-n-alpha-2 border-n-brand text-n-brand'
                  : 'border-n-weak text-n-slate-11 bg-n-solid-2 hover:bg-n-alpha-1 hover:border-n-slate-7'
              "
              @click="applyQuickFilter(qf)"
            >
              <fluent-icon :icon="qf.icon" size="14" />
              <span>{{ t(qf.label) }}</span>
            </button>
          </div>
        </div>

        <div
          class="grid max-h-[70vh] grid-cols-1 gap-4 overflow-y-auto md:grid-cols-2 md:overflow-hidden"
        >
          <!-- Filter Controls -->
          <div
            class="space-y-2 overflow-visible md:overflow-y-auto md:border-r md:border-n-weak md:pr-4"
          >
            <!-- NOOVI: Filtro por Label (etiqueta da conversa vinculada).
                 Só no escopo global do board — o filtro por-coluna é
                 client-side e não trata labels. -->
            <div
              v-if="scope !== 'column' && labelOptions.length > 0"
              class="p-2 bg-n-solid-2 rounded-lg"
            >
              <label class="text-label text-n-slate-12 mb-1 block">
                {{ t('PIPELINE.FILTER.LABELS.LABEL') }}
              </label>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="label in labelOptions"
                  :key="label.id"
                  type="button"
                  :aria-pressed="filters.labels.includes(label.title)"
                  class="inline-flex min-h-11 items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors duration-150 ease-in-out motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                  :class="
                    filters.labels.includes(label.title)
                      ? 'font-semibold border-n-slate-7 bg-n-alpha-2 text-n-slate-12'
                      : 'font-medium border-n-weak text-n-slate-11 bg-n-solid-2 hover:bg-n-alpha-1'
                  "
                  @click="toggleLabel(label.title)"
                >
                  <span
                    class="w-2 h-2 rounded-full flex-shrink-0"
                    :class="stageColorClasses(label.color).dot"
                    :style="stageColorStyles(label.color, 'dot')"
                  />
                  {{ label.title }}
                </button>
              </div>
            </div>

            <!-- Stage Filter (B4 - Advanced Filters) -->
            <div
              v-if="availableStages.length > 0"
              class="p-2 bg-n-solid-2 rounded-lg"
            >
              <label class="text-label text-n-slate-12 mb-1 block">
                {{ t('PIPELINE.STAGE.LABEL') }}
              </label>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="stage in availableStages"
                  :key="stage.id"
                  type="button"
                  :aria-pressed="filters.stages.includes(stage.id)"
                  class="inline-flex min-h-11 items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors duration-150 ease-in-out motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                  :class="
                    filters.stages.includes(stage.id)
                      ? ['font-semibold', stageColorClasses(stage.color).badge]
                      : 'font-medium border-n-weak text-n-slate-11 bg-n-solid-2 hover:bg-n-alpha-1'
                  "
                  :style="
                    filters.stages.includes(stage.id)
                      ? stageColorStyles(stage.color, 'badge')
                      : undefined
                  "
                  @click="
                    filters.stages = filters.stages.includes(stage.id)
                      ? filters.stages.filter(s => s !== stage.id)
                      : [...filters.stages, stage.id]
                  "
                >
                  <span
                    class="w-2 h-2 rounded-full flex-shrink-0"
                    :class="stageColorClasses(stage.color).dot"
                    :style="stageColorStyles(stage.color, 'dot')"
                  />
                  {{ stage.name }}
                </button>
              </div>
            </div>

            <!-- Prioridade -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label class="text-label text-n-slate-12 mb-1 block">
                {{ t('PIPELINE.FORM.PRIORITY.LABEL') }}
              </label>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="option in priorityOptions"
                  :key="option.value"
                  type="button"
                  :aria-pressed="filters.priority.includes(option.value)"
                  class="inline-flex min-h-11 items-center gap-1.5 rounded-lg border px-3 py-1 text-sm font-medium transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                  :class="[
                    filters.priority.includes(option.value)
                      ? option.colors.selected
                      : [option.colors.border, option.colors.hover],
                    option.colors.text,
                  ]"
                  @click="
                    filters.priority = filters.priority.includes(option.value)
                      ? filters.priority.filter(p => p !== option.value)
                      : [...filters.priority, option.value]
                  "
                >
                  <span
                    class="w-2 h-2 rounded-full flex-shrink-0"
                    :class="option.colors.dot"
                  />
                  {{ option.label }}
                </button>
              </div>
            </div>

            <!-- Status Filter -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label class="text-label text-n-slate-12 mb-1 block">
                {{ t('PIPELINE.FILTERS.STATUS.LABEL') }}
              </label>
              <div class="flex flex-wrap gap-1">
                <button
                  type="button"
                  :aria-pressed="filters.status === null"
                  class="min-h-11 rounded-lg border px-3 py-1 text-sm font-medium transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                  :class="[
                    filters.status === null
                      ? 'bg-n-alpha-2 border-n-slate-7 text-n-slate-12'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1',
                  ]"
                  @click="filters.status = null"
                >
                  {{ t('PIPELINE.FILTERS.STATUS.ALL') }}
                </button>
                <button
                  type="button"
                  :aria-pressed="filters.status === 'open'"
                  class="min-h-11 rounded-lg border px-3 py-1 text-sm font-medium transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                  :class="[
                    filters.status === 'open'
                      ? 'bg-n-blue-3 border-n-blue-9 text-n-blue-11'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1',
                  ]"
                  @click="filters.status = 'open'"
                >
                  {{ t('PIPELINE.FILTERS.STATUS.OPEN') }}
                </button>
                <button
                  type="button"
                  :aria-pressed="filters.status === 'won'"
                  class="min-h-11 rounded-lg border px-3 py-1 text-sm font-medium transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                  :class="[
                    filters.status === 'won'
                      ? 'bg-n-teal-3 border-n-teal-9 text-n-teal-11'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1',
                  ]"
                  @click="filters.status = 'won'"
                >
                  {{ t('PIPELINE.FILTERS.STATUS.WON') }}
                </button>
                <button
                  type="button"
                  :aria-pressed="filters.status === 'lost'"
                  class="min-h-11 rounded-lg border px-3 py-1 text-sm font-medium transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                  :class="[
                    filters.status === 'lost'
                      ? 'bg-n-ruby-3 border-n-ruby-9 text-n-ruby-11'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1',
                  ]"
                  @click="filters.status = 'lost'"
                >
                  {{ t('PIPELINE.FILTERS.STATUS.LOST') }}
                </button>
              </div>
            </div>

            <!-- Fase 2.1 — Filtro SLA excedido -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label class="flex items-start gap-2 cursor-pointer">
                <input
                  :id="fieldId('sla-exceeded')"
                  v-model="filters.sla_exceeded"
                  name="sla_exceeded"
                  type="checkbox"
                  class="mt-0.5 h-4 w-4 rounded border-n-weak text-red-600 focus:ring-red-500 cursor-pointer"
                />
                <div class="flex-1">
                  <span class="text-label text-n-slate-12 block">
                    <fluent-icon
                      icon="warning"
                      size="14"
                      class="inline-block text-red-500"
                    />
                    {{ t('PIPELINE.SLA.FILTER_LABEL') }}
                  </span>
                  <span class="text-xs text-n-slate-10 block mt-1">
                    {{ t('PIPELINE.SLA.FILTER_HELP') }}
                  </span>
                </div>
              </label>
            </div>

            <!-- Valor -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label
                :for="fieldId('value-min')"
                class="text-label text-n-slate-12 mb-1 block"
              >
                {{ t('PIPELINE.VALUE.LABEL') }}
              </label>
              <div class="flex gap-2">
                <div class="flex-1">
                  <FormInput
                    :id="fieldId('value-min')"
                    v-model="filters.value.min"
                    name="value_min"
                    type="number"
                    :placeholder="t('PIPELINE.VALUE.MIN')"
                    :aria-label="`${t('PIPELINE.VALUE.LABEL')}: ${t('PIPELINE.VALUE.MIN')}`"
                  />
                </div>
                <div class="flex-1">
                  <FormInput
                    :id="fieldId('value-max')"
                    v-model="filters.value.max"
                    name="value_max"
                    type="number"
                    :placeholder="t('PIPELINE.VALUE.MAX')"
                    :aria-label="`${t('PIPELINE.VALUE.LABEL')}: ${t('PIPELINE.VALUE.MAX')}`"
                  />
                </div>
              </div>
            </div>

            <!-- Agente Responsável -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label
                :for="fieldId('agent')"
                class="text-label text-n-slate-12 mb-1 block"
              >
                {{ t('PIPELINE.AGENT.LABEL') }}
              </label>
              <Select
                v-model="selectedAgentId"
                :input-id="fieldId('agent')"
                name="agent_id"
                class="w-full [&>select]:w-full"
                :options="agentOptions"
                :aria-label="t('PIPELINE.AGENT.LABEL')"
              />
            </div>

            <!-- Data de Criação -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label
                :for="fieldId('date-start')"
                class="text-label text-n-slate-12 mb-1 block"
              >
                {{ t('PIPELINE.FILTER.DATE.LABEL') }}
              </label>
              <div class="flex gap-2">
                <div class="flex-1 date-picker no-margin">
                  <DatePicker
                    v-model:value="filters.date.start"
                    type="date"
                    format="DD/MM/YYYY"
                    value-type="YYYY-MM-DD"
                    :placeholder="t('PIPELINE.FILTER.DATE.START')"
                    :input-attr="{
                      id: fieldId('date-start'),
                      name: 'date_start',
                      'aria-label': `${t('PIPELINE.FILTER.DATE.LABEL')}: ${t('PIPELINE.FILTER.DATE.START')}`,
                    }"
                  />
                </div>
                <div class="flex-1 date-picker no-margin">
                  <DatePicker
                    v-model:value="filters.date.end"
                    type="date"
                    format="DD/MM/YYYY"
                    value-type="YYYY-MM-DD"
                    :placeholder="t('PIPELINE.FILTER.DATE.END')"
                    :input-attr="{
                      id: fieldId('date-end'),
                      name: 'date_end',
                      'aria-label': `${t('PIPELINE.FILTER.DATE.LABEL')}: ${t('PIPELINE.FILTER.DATE.END')}`,
                    }"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Preset Management -->
          <div class="space-y-3 overflow-y-auto">
            <h4
              class="text-heading-3 text-n-slate-12 border-b border-n-weak pb-1 mb-2"
            >
              {{ t('PIPELINE.PRESET.TITLE') }}
            </h4>

            <!-- Save Current -->
            <div class="space-y-1">
              <label
                :for="fieldId('preset-name')"
                class="text-label text-n-slate-12 block"
              >
                {{ t('PIPELINE.PRESET.SAVE_CURRENT') }}
              </label>
              <div class="flex gap-1">
                <div class="flex-1">
                  <FormInput
                    :id="fieldId('preset-name')"
                    v-model="newPresetName"
                    name="preset_name"
                    type="text"
                    :placeholder="t('PIPELINE.PRESET.NAME_PLACEHOLDER')"
                    @keyup.enter="saveCurrentPreset"
                  />
                </div>
                <button
                  type="button"
                  :aria-label="t('PIPELINE.PRESET.SAVE_CURRENT')"
                  :disabled="!newPresetName.trim()"
                  class="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9 disabled:cursor-not-allowed disabled:opacity-50"
                  @click="saveCurrentPreset"
                >
                  <fluent-icon icon="save" size="16" />
                </button>
              </div>
            </div>

            <!-- Saved Presets List -->
            <div class="space-y-1">
              <label class="text-label text-n-slate-12 block">
                {{ t('PIPELINE.PRESET.SAVED_PRESETS') }}
              </label>
              <div
                v-if="Object.keys(savedPresets).length === 0"
                class="text-xs text-n-slate-10 p-2 text-center"
              >
                {{ t('PIPELINE.PRESET.NO_PRESETS') }}
              </div>
              <ul
                v-else
                class="max-h-48 space-y-1 overflow-y-auto pr-1 [scrollbar-color:var(--n-brand)_var(--n-alpha-1)] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-n-brand [&::-webkit-scrollbar-track]:rounded-[3px] [&::-webkit-scrollbar-track]:bg-n-alpha-1"
              >
                <li
                  v-for="(presetFilters, name) in savedPresets"
                  :key="name"
                  class="flex justify-between items-center p-1.5 rounded-md flex-wrap"
                  :class="{
                    'bg-n-alpha-2': appliedPresetName === name,
                    'hover:bg-n-alpha-1': appliedPresetName !== name,
                  }"
                >
                  <div class="flex-1 min-w-0 mr-2">
                    <span
                      class="text-sm font-medium text-n-slate-12 block truncate"
                      :title="name"
                      >{{ name }}</span
                    >
                    <span
                      class="text-xs text-n-slate-10 block truncate"
                      :title="getPresetSummary(presetFilters)"
                    >
                      {{ getPresetSummary(presetFilters) }}
                    </span>
                  </div>
                  <div class="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      :title="t('PIPELINE.PRESET.APPLY')"
                      :aria-label="t('PIPELINE.PRESET.APPLY')"
                      class="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                      @click="applyPreset(name)"
                    >
                      <fluent-icon icon="play-circle" size="14" />
                    </button>
                    <button
                      type="button"
                      :title="t('PIPELINE.PRESET.DELETE')"
                      :aria-label="t('PIPELINE.PRESET.DELETE')"
                      class="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-n-ruby-9 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-ruby-9"
                      @click="deletePreset(name)"
                    >
                      <fluent-icon icon="delete" size="14" />
                    </button>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <footer
          class="flex justify-end space-x-2 pt-3 mt-3 border-t border-n-weak"
        >
          <button
            type="button"
            class="min-h-11 rounded-lg px-4 py-2 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
            @click="handleClear"
          >
            {{ t('PIPELINE.FORM.CLEAR') }}
          </button>
          <button
            type="button"
            class="min-h-11 rounded-lg bg-n-brand px-4 py-2 text-white transition-colors hover:bg-n-brand/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-blue-9"
            @click="handleApply"
          >
            {{ t('PIPELINE.FORM.APPLY') }}
          </button>
        </footer>
      </div>
    </Modal>

    <Dialog
      ref="overwriteDialogRef"
      type="edit"
      :title="t('PIPELINE.PRESET.OVERWRITE_TITLE')"
      :description="
        t('PIPELINE.PRESET.CONFIRM_OVERWRITE', { name: pendingPresetName })
      "
      :confirm-button-label="t('PIPELINE.PRESET.OVERWRITE_CONFIRM')"
      @confirm="confirmOverwritePreset"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('PIPELINE.PRESET.DELETE_TITLE')"
      :description="
        t('PIPELINE.PRESET.CONFIRM_DELETE', { name: pendingPresetName })
      "
      :confirm-button-label="t('PIPELINE.PRESET.DELETE')"
      @confirm="confirmDeletePreset"
    />
  </Teleport>
</template>
